import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  ArrowLeftRight, BadgeCheck, BookOpen, Boxes, Building2, CircleDollarSign, ExternalLink,
  House, LayoutDashboard, ScrollText, Settings as SettingsIcon, Shield, Wrench, Zap, Eclipse, Moon, Sun,
  type LucideIcon,
} from "lucide-react";
import { Button, Select, Spinner, ToastProvider } from "@polaris/ui";
import "@polaris/ui/src/styles.css";
import { installErrorReporting, queryClient, SessionProvider, useApi, useLiveEvents, useSession, useTenant } from "./lib";
import { getLang, languages, setLang, t } from "./i18n";
import { TenantSelect } from "./shared";
const Home = React.lazy(() => import("./pages/Home"));
const Tenants = React.lazy(() => import("./pages/Tenants"));
const Approvals = React.lazy(() => import("./pages/Approvals"));
const Models = React.lazy(() => import("./pages/Models"));
const Aliases = React.lazy(() => import("./pages/Aliases"));
const Policies = React.lazy(() => import("./pages/Policies"));
const Mcp = React.lazy(() => import("./pages/Mcp"));
const Costs = React.lazy(() => import("./pages/Costs"));
const Audit = React.lazy(() => import("./pages/Audit"));
const Settings = React.lazy(() => import("./pages/Settings"));

const TokenSavings = React.lazy(() => import("./pages/TokenSavings"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));

// anyOf: o item aparece para quem tem qualquer uma das áreas (economia de tokens é da
// plataforma, que configura os modelos, e do FinOps, que acompanha o valor).
const nav: { area: string; anyOf?: string[]; to: string; label: string; icon: LucideIcon }[] = [
  { area: "home", to: "/", label: "Início", icon: House },
  { area: "observability", to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { area: "tenants", to: "/tenants", label: "Tenants", icon: Building2 },
  { area: "approvals", to: "/approvals", label: "Aprovações", icon: BadgeCheck },
  { area: "models", to: "/models", label: "Modelos", icon: Boxes },
  { area: "models", anyOf: ["models", "costs"], to: "/token-savings", label: "Economia de tokens", icon: Zap },
  { area: "aliases", to: "/aliases", label: "Aliases", icon: ArrowLeftRight },
  { area: "policies", to: "/policies", label: "Políticas", icon: Shield },
  { area: "mcp", to: "/mcp", label: "Ferramentas MCP", icon: Wrench },
  { area: "costs", to: "/costs", label: "Custos", icon: CircleDollarSign },
  { area: "audit", to: "/audit", label: "Auditoria e logs", icon: ScrollText },
  { area: "settings", to: "/settings", label: "Plataforma", icon: SettingsIcon },
];

function Banner() {
  const { data } = useApi<any>("/api/v1/status", undefined, { refetchInterval: 30_000 });
  if (!data?.degraded) return null;
  const parts: string[] = [];
  if (data.open_breakers?.length) parts.push(t("circuito aberto em {d}", { d: data.open_breakers.join(", ") }));
  if (data.stale_replicas) parts.push(t("réplicas com configuração desatualizada"));
  (data.incidents ?? []).forEach((i: any) => parts.push(`${i.severity?.toUpperCase()} ${i.title}`));
  return (
    <div className="banner" role="alert">
      <strong>{t("Degradação em andamento")}:</strong> {parts.join(" · ")}
    </div>
  );
}

function prefersDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

const themes = ["light", "dark", "graphite"] as const;
type ThemeName = (typeof themes)[number];

function isTheme(value?: string): value is ThemeName {
  return value === "light" || value === "dark" || value === "graphite";
}

function nextTheme(shown: ThemeName): ThemeName {
  if (shown === "light") return "dark";
  if (shown === "dark") return "graphite";
  return "light";
}

function themeLabel(shown: ThemeName) {
  if (shown === "graphite") return t("Grafite");
  if (shown === "dark") return t("Escuro");
  return t("Claro");
}

function Theme() {
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem("polaris:theme") ?? "";
      if (isTheme(saved)) return saved;
    } catch { /* armazenamento indisponível */ }
    return "";
  });
  const [systemDark, setSystemDark] = useState(prefersDark);
  useEffect(() => {
    if (isTheme(theme)) document.documentElement.dataset.theme = theme;
    else delete document.documentElement.dataset.theme;
    try { localStorage.setItem("polaris:theme", theme); } catch { /* ok */ }
  }, [theme]);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const shown: ThemeName = isTheme(theme) ? theme : (systemDark ? "dark" : "light");
  const label = themeLabel(shown);
  return (
    <Button small variant="ghost" className="theme-toggle" aria-label={label} title={label} onClick={() => setTheme(nextTheme(shown))}>
      {shown === "graphite" ? <Eclipse aria-hidden="true" /> : shown === "dark" ? <Moon aria-hidden="true" /> : <Sun aria-hidden="true" />}
    </Button>
  );
}

function initials(name?: string) {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (parts[0][0] + last).toUpperCase();
}

function Shell() {
  const { me, areas, logout } = useSession();
  const [tenant, setTenant] = useTenant();
  const loc = useLocation();
  const [, force] = useState(0);
  const pending = useApi<any>("/api/v1/approvals", { status: "pending", mine: "decide", limit: 1 }, { refetchInterval: 60_000 });
  useLiveEvents();
  const keep = (to: string) => (tenant ? `${to}?tenant=${encodeURIComponent(tenant)}` : to);
  const visible = nav.filter((n) => (n.anyOf ?? [n.area]).some((a) => areas.includes(a)));
  useEffect(() => { document.getElementById("main")?.focus(); }, [loc.pathname]);
  return (
    <div className="shell">
      <a className="skip" href="#main">{t("Pular para o conteúdo")}</a>
      <aside className="side console-side">
        <div className="brand">
          <img className="brand-logo" src="/polaris-mark.png" alt="" width={46} height={46} decoding="async" />
          <span>Polaris Console<small>{t("Governança de IA")}</small></span>
        </div>
        <nav className="nav" aria-label={t("Áreas do console")}>
          {visible.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={keep(n.to)} end={n.to === "/"}>
                <Icon aria-hidden="true" />
                <span>{t(n.label)}</span>
                {n.area === "approvals" && (pending.data?.total ?? 0) > 0 && <span className="count" aria-label={t("{n} pendentes", { n: pending.data.total })}>{pending.data.total}</span>}
              </NavLink>
            );
          })}
        </nav>
        {/* Documentação da Admin API (Swagger UI servido pelo próprio console, com a sessão atual). */}
        <a className="nav-doc" href="/api-docs/" target="_blank" rel="noreferrer">
          <BookOpen aria-hidden="true" />
          <span>{t("Admin API (Swagger)")}</span>
          <ExternalLink aria-hidden="true" />
        </a>
      </aside>
      <div className="main">
        <header className="top">
          <div className="top-inner">
          <label className="top-tenant" htmlFor="console-tenant">
            <span>{t("Tenant")}</span>
            <TenantSelect id="console-tenant" value={tenant} onChange={setTenant} allowEmpty />
          </label>
          <span className="spacer" />
          <div className="top-tools">
            <Theme />
            <Select
              className="lang-select"
              aria-label={t("Idioma")}
              value={getLang()}
              onChange={(e) => { setLang(e.target.value); force((x) => x + 1); }}
            >
              {languages.map((l) => (
                <option key={l.id} value={l.id}>
                  <img className="lang-flag-img" src={l.flag} alt="" />
                  {l.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="top-user" title={me.principal?.email}>
            <span className="top-avatar" aria-hidden="true">{initials(me.principal?.name)}</span>
            <span className="top-user-text">
              <span className="top-user-name">{me.principal?.name}</span>
              <span className="top-user-role">{(me.principal?.roles ?? []).join(", ")}</span>
            </span>
          </div>
          <Button small variant="ghost" onClick={logout}>{t("Sair")}</Button>
          </div>
        </header>
        <Banner />
        <main id="main" className="content" tabIndex={-1}>
          <React.Suspense fallback={<Spinner />}>
          <Routes>
            <Route path="/" element={<Home />} />
            {areas.includes("observability") && <Route path="/dashboard" element={<Dashboard />} />}
            {areas.includes("tenants") && <Route path="/tenants/*" element={<Tenants />} />}
            <Route path="/approvals/*" element={<Approvals />} />
            {areas.includes("models") && <Route path="/models/*" element={<Models />} />}
            {(areas.includes("models") || areas.includes("costs")) && <Route path="/token-savings" element={<TokenSavings />} />}
            {areas.includes("aliases") && <Route path="/aliases/*" element={<Aliases />} />}
            {areas.includes("policies") && <Route path="/policies/*" element={<Policies />} />}
            {areas.includes("mcp") && <Route path="/mcp/*" element={<Mcp />} />}
            {areas.includes("costs") && <Route path="/costs/*" element={<Costs />} />}
            {areas.includes("audit") && <Route path="/audit/*" element={<Audit />} />}
            {areas.includes("settings") && <Route path="/settings/*" element={<Settings />} />}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </React.Suspense>
        </main>
      </div>
    </div>
  );
}

installErrorReporting();
document.documentElement.lang = getLang();
createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <SessionProvider>
            <Shell />
          </SessionProvider>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
